(() => {
  'use strict';

  const STORAGE_KEY = 'md_energeez_compare';
  const LEGACY_KEY = 'm-compare-products';
  const MAX_PRODUCTS = 3;
  const COMPARE_PAGE_TEMPLATE = 'page.product-compare';


  // Behoud de actieve Shopify-taal/markt bij storefront-links.
  const localizeStorefrontUrl = (url) => {
    const value = String(url || '').trim();
    if (!value) return '';

    let parsed;
    try {
      parsed = new URL(value, window.location.origin);
    } catch (error) {
      return value;
    }

    // Externe links niet aanpassen.
    if (parsed.origin !== window.location.origin) return value;

    const root = String(window.Shopify?.routes?.root || '/');
    const localizedRoot = root.endsWith('/') ? root : `${root}/`;
    const localizedRootPath = localizedRoot === '/' ? '' : localizedRoot.replace(/\/$/, '');

    // URL bevat de actieve taalprefix al.
    if (
      localizedRootPath &&
      (parsed.pathname === localizedRootPath || parsed.pathname.startsWith(`${localizedRootPath}/`))
    ) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }

    const path = parsed.pathname.replace(/^\/+/, '');
    return `${localizedRoot}${path}${parsed.search}${parsed.hash}`;
  };
  const t = (key, fallback = '', replacements = {}) => window.MDT ? window.MDT(key, fallback, replacements) : fallback;

  const esc = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  class MDEnergeezCompare {
    constructor() {
      this.items = [];
      this.isComparePage = window.MinimogSettings?.template === COMPARE_PAGE_TEMPLATE || Boolean(document.querySelector('[data-md-compare-page]'));
      this.load();
      this.bind();
      this.syncUI();
    }

    load() {
      let stored = null;
      try {
        stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
      } catch (error) {
        stored = null;
      }

      if (Array.isArray(stored?.items)) {
        this.items = stored.items
          .filter((item) => item && item.handle)
          .slice(0, MAX_PRODUCTS)
          .map((item) => ({
            handle: String(item.handle),
            title: String(item.title || item.handle),
            image: String(item.image || ''),
            url: String(item.url || `/products/${item.handle}`),
          }));
        return;
      }

      // Eenmalige migratie van de ingebouwde Minimog-vergelijklijst.
      try {
        const legacy = JSON.parse(window.localStorage.getItem(LEGACY_KEY) || '[]');
        if (Array.isArray(legacy) && legacy.length) {
          this.items = [...new Set(legacy)]
            .filter(Boolean)
            .slice(0, MAX_PRODUCTS)
            .map((handle) => ({ handle: String(handle), title: String(handle), image: '', url: `/products/${handle}` }));
          this.persist();
        }
      } catch (error) {
        this.items = [];
      }
    }

    persist() {
      try {
        if (!this.items.length) {
          window.localStorage.removeItem(STORAGE_KEY);
          window.localStorage.removeItem(LEGACY_KEY);
          return;
        }
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, items: this.items, updatedAt: Date.now() }));
        // Houd de oude Minimog-teller/onderdelen compatibel.
        window.localStorage.setItem(LEGACY_KEY, JSON.stringify(this.items.map((item) => item.handle)));
      } catch (error) {
        // Opslag mag vergelijken nooit blokkeren.
      }
    }

    bind() {
      document.addEventListener('click', (event) => {
        const mdButton = event.target.closest('[data-md-compare-button]');
        const legacyButton = event.target.closest('.m-compare-button');
        const removeButton = event.target.closest('[data-md-compare-remove]');
        const clearButton = event.target.closest('[data-md-compare-clear]');

        if (removeButton) {
          event.preventDefault();
          this.remove(removeButton.dataset.productHandle);
          return;
        }

        if (clearButton) {
          event.preventDefault();
          this.clear();
          return;
        }

        const button = mdButton || legacyButton;
        if (!button) return;

        const handle = button.dataset.productHandle;
        if (!handle) return;

        event.preventDefault();
        this.toggle({
          handle,
          title: button.dataset.productTitle || handle,
          image: button.dataset.productImage || '',
          url: button.dataset.productUrl || `/products/${handle}`,
        });
      });

      document.addEventListener('shopify:section:load', () => this.syncUI());
      window.addEventListener('resize', () => {
        const tray = document.querySelector('[data-md-compare-tray]');
        if (tray) this.updateTrayOffset(tray);
      });
    }

    contains(handle) {
      return this.items.some((item) => item.handle === handle);
    }

    toggle(item) {
      if (this.contains(item.handle)) {
        this.remove(item.handle);
        return;
      }

      if (this.items.length >= MAX_PRODUCTS) {
        this.toast(t('compare.maxProducts', 'You can compare up to 3 products at once.'));
        return;
      }

      this.items.push(item);
      this.commit();
    }

    remove(handle) {
      this.items = this.items.filter((item) => item.handle !== handle);
      this.commit();
    }

    clear() {
      this.items = [];
      this.commit();
    }

    pruneMissing(handles = []) {
      const missing = new Set(handles.map((handle) => String(handle || '')).filter(Boolean));
      if (!missing.size) return;

      const originalLength = this.items.length;
      this.items = this.items.filter((item) => !missing.has(item.handle));
      if (this.items.length === originalLength) return;

      this.persist();
      this.syncUI();
    }

    commit() {
      this.persist();
      this.syncUI();
      document.dispatchEvent(new CustomEvent('md:compare:change', { detail: { items: [...this.items] } }));
    }

    syncUI() {
      const handles = new Set(this.items.map((item) => item.handle));

      document.querySelectorAll('[data-md-compare-button], .m-compare-button').forEach((button) => {
        const active = handles.has(button.dataset.productHandle);
        button.classList.toggle('is-active', active);
        button.classList.toggle('added-to-compare', active);
        button.setAttribute('aria-pressed', String(active));

        const text = button.querySelector('[data-md-compare-text], .m-compare-button-text');
        if (text) {
          const addText = text.dataset.addText || t('compare.add', 'Compare');
          const removeText = text.dataset.removeText || t('compare.remove', 'Remove from comparison');
          text.textContent = active ? removeText : addText;
        }
      });

      document.querySelectorAll('.m-compare-count, [data-md-compare-count]').forEach((count) => {
        count.textContent = String(this.items.length);
        count.classList.toggle('m:hidden', this.items.length === 0);
        count.hidden = this.items.length === 0;
      });

      this.renderTray();
    }

    compareUrl() {
      const configured = document.querySelector('[data-md-compare-page-url]')?.dataset.mdComparePageUrl;
      if (configured) return localizeStorefrontUrl(configured);
      const headerLink = document.querySelector('.m-header__compare');
      if (headerLink?.href) return localizeStorefrontUrl(headerLink.href);
      return localizeStorefrontUrl('/pages/vergelijken');
    }

    updateTrayOffset(tray) {
      const height = tray ? Math.ceil(tray.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty('--md-compare-tray-height', `${height}px`);
      document.body.classList.toggle('md-compare-tray-active', height > 0);
    }

    clearTrayOffset() {
      document.documentElement.style.setProperty('--md-compare-tray-height', '0px');
      document.body.classList.remove('md-compare-tray-active');
    }

    renderTray() {
      let tray = document.querySelector('[data-md-compare-tray]');
      if (this.isComparePage || !this.items.length) {
        tray?.remove();
        this.clearTrayOffset();
        return;
      }

      if (!tray) {
        tray = document.createElement('aside');
        tray.className = 'md-compare-tray';
        tray.setAttribute('data-md-compare-tray', '');
        tray.setAttribute('aria-label', t('compare.trayLabel', 'Selected products to compare'));
        document.body.appendChild(tray);
      }

      const productCards = this.items.map((item) => `
        <div class="md-compare-tray__item">
          <span class="md-compare-tray__thumb">${item.image ? `<img src="${esc(item.image)}" alt="" width="44" height="44" loading="lazy">` : '<span aria-hidden="true">↔</span>'}</span>
          <span class="md-compare-tray__copy"><strong>${esc(item.title)}</strong></span>
          <button type="button" class="md-compare-tray__remove" data-md-compare-remove data-product-handle="${esc(item.handle)}" aria-label="${esc(t('compare.removeProduct', 'Remove {title}', { title: item.title }))}">×</button>
        </div>
      `).join('');

      const canCompare = this.items.length >= 2;
      tray.innerHTML = `
        <div class="md-compare-tray__inner">
          <div class="md-compare-tray__summary">
            <span class="md-compare-tray__icon" aria-hidden="true">↔</span>
            <span><strong>${esc(t(this.items.length === 1 ? 'compare.selectedOne' : 'compare.selectedOther', '{count} products selected', { count: this.items.length }))}</strong><small>${esc(canCompare ? t('compare.ready', 'Ready to compare technical differences.') : t('compare.selectMore', 'Select at least one more product.'))}</small></span>
          </div>
          <div class="md-compare-tray__items">${productCards}</div>
          <div class="md-compare-tray__actions">
            <button type="button" class="md-compare-tray__clear" data-md-compare-clear>${esc(t('compare.clearAll', 'Clear all'))}</button>
            <a class="md-compare-tray__button${canCompare ? '' : ' is-disabled'}" href="${canCompare ? esc(this.compareUrl()) : '#'}" ${canCompare ? '' : 'aria-disabled="true" tabindex="-1"'}>${esc(t('compare.compareProducts', 'Compare {count} products', { count: this.items.length }))} <span aria-hidden="true">→</span></a>
          </div>
        </div>
      `;

      window.requestAnimationFrame(() => this.updateTrayOffset(tray));
    }

    toast(message) {
      let toast = document.querySelector('[data-md-compare-toast]');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'md-compare-toast';
        toast.dataset.mdCompareToast = '';
        toast.setAttribute('role', 'status');
        document.body.appendChild(toast);
      }
      toast.textContent = message;
      toast.classList.add('is-visible');
      window.clearTimeout(this.toastTimer);
      this.toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 3200);
    }

    getItems() {
      return [...this.items];
    }
  }

  window.MDEnergeezCompare = new MDEnergeezCompare();
})();
